import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { clampPoints, maxRedeemablePoints, POINT_VALUE_INR } from "../lib/pricing";

type Props = {
  total: number;
  availablePoints: number;
  points: number;
  earnPoints?: number;
  onPointsChange: (points: number) => void;
  pointValue?: number;
  currency?: string;
};

export function PointsCashPay({
  total,
  availablePoints,
  points,
  earnPoints,
  onPointsChange,
  pointValue = POINT_VALUE_INR,
  currency = "AED",
}: Props) {
  const max = maxRedeemablePoints(total, availablePoints, pointValue);
  const step = max >= 500 ? 50 : 10;
  const cash = Math.max(0, total - points * pointValue);
  const money = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: pointValue < 1 ? 2 : 0,
    }).format(amount);
  const balanceAfter = earnPoints == null ? null : availablePoints - points + earnPoints;

  return (
    <section className="rounded-lg border border-[#e7e7e7] bg-white p-5">
      <h2 className="text-lg font-bold text-[#1a1a1a]">Pay with cash and points</h2>
      <p className="mt-1 text-sm text-[#595959]">
        1 point equals {money(pointValue)}. You can cover up to half this fare with
        points. The rest is charged in cash.
      </p>

      <div className="mt-5 flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#595959]">
            Points to use
          </p>
          <p className="text-2xl font-bold text-[#003580]">
            {points.toLocaleString("en-IN")}
          </p>
        </div>
        <p className="text-sm font-semibold text-[#008009]">−{money(points * pointValue)}</p>
      </div>

      <Slider
        className="mt-4"
        min={0}
        max={Math.max(max, 1)}
        step={step}
        value={[points]}
        disabled={max === 0}
        onValueChange={([value]) =>
          onPointsChange(clampPoints(value, total, availablePoints, pointValue))
        }
        aria-label="Points to apply"
      />
      <div className="mt-1 flex justify-between text-xs text-[#595959]">
        <span>Cash only</span>
        <span>Max {max.toLocaleString("en-IN")} pts</span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => onPointsChange(0)}
        >
          Cash only
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          disabled={max === 0}
          onClick={() => onPointsChange(max)}
        >
          Use maximum points
        </Button>
      </div>

      <dl className="mt-5 space-y-2 border-t border-[#e7e7e7] pt-4 text-sm">
        <Row label="Fare due" value={money(total)} />
        <Row label="Points applied" value={`−${money(points * pointValue)}`} />
        <div className="flex items-center justify-between pt-1 text-base font-bold">
          <dt>Cash to pay</dt>
          <dd>{money(cash)}</dd>
        </div>
        {earnPoints == null ? null : (
          <Row
            label="Points you'll earn"
            value={`+${earnPoints.toLocaleString("en-IN")}`}
          />
        )}
      </dl>
      <p className="mt-3 text-xs text-[#595959]">
        {balanceAfter == null
          ? `Points are added when you confirm. A stay can stay pending until after checkout, so those points are not spendable yet. You have ${availablePoints.toLocaleString("en-IN")} points available now.`
          : `Balance after this booking: ${balanceAfter.toLocaleString("en-IN")} points. You have ${availablePoints.toLocaleString("en-IN")} points now.`}
      </p>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[#1a1a1a]">
      <dt className="text-[#595959]">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
